// Wiki page editor — markdown textarea + live preview side-by-side.
// Used in two modes:
//   - edit: load an existing page, edit body/title, save via PATCH
//   - new:  blank slate; pick category + title; save creates the page

import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Loader2, Save, Eye, Pencil, Trash2 } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

type Mode = { kind: 'edit'; slug: string } | { kind: 'new' };

const CATEGORY_OPTIONS = [
  /* Client Brain dossier sections. `campaign` is deliberately absent — those
   * pages are created by the platform sync, not by hand. */
  { v: 'company', label: 'Company & business' },
  { v: 'strategy', label: 'Strategic priority' },
  { v: 'economics', label: 'Marketing economics' },
  { v: 'market', label: 'Market & category' },
  { v: 'stakeholder', label: 'Stakeholder' },
  { v: 'hypothesis', label: 'Hypothesis' },
  { v: 'learning', label: 'Learning' },
  /* Brand sections */
  { v: 'identity', label: 'Brand identity' },
  { v: 'voice', label: 'Voice & tone' },
  { v: 'visual', label: 'Visual identity' },
  { v: 'audience', label: 'Audience' },
  { v: 'persona', label: 'Persona' },
  { v: 'competitor', label: 'Competitor' },
  { v: 'pillar', label: 'Content pillar' },
  { v: 'product', label: 'Product' },
  { v: 'channel', label: 'Channel' },
  { v: 'note', label: 'Note' },
];

interface WikiPageEditorProps {
  mode: Mode;
  brandId?: string;
  onClose: () => void;
  onSaved: (slug: string) => void;
  onDeleted?: (slug: string) => void;
}

export function WikiPageEditor({ mode, brandId = 'default', onClose, onSaved, onDeleted }: WikiPageEditorProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string>('note');
  const [bodyMd, setBodyMd] = useState('');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [loading, setLoading] = useState(mode.kind === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode.kind !== 'edit') return;
    coworkApi.wiki.get(mode.slug, brandId)
      .then(p => { setTitle(p.title); setCategory(p.category); setBodyMd(p.bodyMd); })
      .catch(e => setError(e.message || 'Failed to load page'))
      .finally(() => setLoading(false));
  }, [mode, brandId]);

  const save = async () => {
    if (!title.trim()) { setError('Title required.'); return; }
    if (!bodyMd.trim()) { setError('Body required.'); return; }
    setError(null);
    setSaving(true);
    try {
      let slug: string;
      if (mode.kind === 'edit') {
        const res = await coworkApi.wiki.save(mode.slug, { title, category, bodyMd, brandId });
        slug = res.slug;
      } else {
        const res = await coworkApi.wiki.create({ category, title, bodyMd, brandId });
        slug = res.slug;
      }
      onSaved(slug);
    } catch (e) {
      setError((e as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (mode.kind !== 'edit') return;
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await coworkApi.wiki.delete(mode.slug, brandId);
      onDeleted?.(mode.slug);
    } catch (e) {
      setError((e as Error).message || 'Delete failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#eaecf0]">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[#101828] shrink-0">
              {mode.kind === 'edit' ? 'Edit page' : 'New page'}
            </div>
            {mode.kind === 'edit' && (
              <code className="text-[11px] text-[#94a3b8] bg-[#f8fafc] px-2 py-0.5 rounded">{mode.slug}</code>
            )}
          </div>
          <div className="flex items-center gap-1">
            {mode.kind === 'edit' && (
              <button
                onClick={remove}
                disabled={saving}
                className="text-[11px] text-[#dc2626] hover:bg-[#fef2f2] px-2 py-1 rounded flex items-center gap-1"
                title="Delete page"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-[#f8fafc]">
              <X className="w-4 h-4 text-[#94a3b8]" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex justify-center items-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#94a3b8]" />
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-[#eaecf0] flex items-center gap-3">
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Page title"
                className="flex-1 text-[14px] font-semibold text-[#101828] focus:outline-none"
              />
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="text-[12px] px-2 py-1 rounded border border-[#e4e7ec] focus:outline-none focus:border-[#be43ff]/50"
              >
                {CATEGORY_OPTIONS.map(o => (
                  <option key={o.v} value={o.v}>{o.label}</option>
                ))}
              </select>
              <div className="flex bg-[#f8fafc] p-0.5 rounded">
                <button
                  onClick={() => setTab('edit')}
                  className={`px-2.5 py-1 text-[11px] rounded ${tab === 'edit' ? 'bg-white shadow-sm text-[#101828]' : 'text-[#667085]'}`}
                >
                  <Pencil className="w-3 h-3 inline mr-1" /> Edit
                </button>
                <button
                  onClick={() => setTab('preview')}
                  className={`px-2.5 py-1 text-[11px] rounded ${tab === 'preview' ? 'bg-white shadow-sm text-[#101828]' : 'text-[#667085]'}`}
                >
                  <Eye className="w-3 h-3 inline mr-1" /> Preview
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              {tab === 'edit' ? (
                <textarea
                  value={bodyMd}
                  onChange={e => setBodyMd(e.target.value)}
                  placeholder="Markdown body: # headings, **bold**, lists, [[other-slug]] for cross-links."
                  className="w-full h-full p-5 font-mono text-[12px] resize-none focus:outline-none"
                />
              ) : (
                <div className="h-full overflow-y-auto p-8 bg-[#fafafa]">
                  <article className="max-w-3xl mx-auto prose prose-sm prose-headings:font-semibold prose-headings:text-[#101828] prose-p:text-[#344054] prose-a:text-[#be43ff]">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyMd || '_(empty)_'}</ReactMarkdown>
                  </article>
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-[#eaecf0] flex items-center gap-3">
              {error && <div className="text-[12px] text-[#dc2626] flex-1">{error}</div>}
              <div className="ml-auto flex gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-[12px] text-[#667085] hover:bg-[#f8fafc] rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-3 py-1.5 text-[12px] font-semibold text-white bg-[#be43ff] hover:bg-[#a93ae0] disabled:opacity-50 rounded-md flex items-center gap-1.5"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {saving ? 'Saving…' : mode.kind === 'edit' ? 'Save changes' : 'Create page'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
